import React from 'react';

/**
 * KidzIM design-system primitives, ported to React from the handoff bundle.
 * Visual rules (radii, colors, shadows, motion) come straight from the design
 * tokens in styles.css. Keep these presentational and prop-driven.
 */

type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

const buttonColors: Record<ButtonVariant, React.CSSProperties> = {
  primary: { background: 'var(--blue-500)', color: '#fff', border: 'none' },
  accent: { background: 'var(--sun-400)', color: 'var(--ink-900)', border: 'none' },
  secondary: { background: 'var(--white)', color: 'var(--ink-800)', border: 'var(--border-width) solid var(--border-default)' },
  ghost: { background: 'transparent', color: 'var(--blue-600)', border: 'none' },
  danger: { background: 'var(--flag-500)', color: '#fff', border: 'none' },
};

const buttonSizes: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '7px 14px', fontSize: 'var(--text-sm)' },
  md: { padding: '11px 20px', fontSize: 'var(--text-md)' },
  lg: { padding: '14px 26px', fontSize: 'var(--text-lg)' },
};

export function Button({
  variant = 'primary',
  size = 'md',
  full = false,
  style,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  full?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={disabled}
      style={{
        ...buttonColors[variant],
        ...buttonSizes[size],
        borderRadius: 'var(--radius-pill)',
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        width: full ? '100%' : undefined,
        boxShadow: variant === 'secondary' ? 'var(--shadow-xs)' : 'var(--shadow-sm)',
        transition: 'transform var(--dur-fast) var(--ease-bounce), background var(--dur-fast)',
        ...style,
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)';
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
      }}
    />
  );
}

export function Input({
  label,
  error,
  style,
  id,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }) {
  const inputId = id ?? rest.name;
  return (
    <div className="kz-stack" style={{ gap: 'var(--space-1)' }}>
      {label && (
        <label className="kz-field-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        {...rest}
        style={{
          font: 'var(--type-body)',
          color: 'var(--ink-900)',
          background: 'var(--white)',
          border: `var(--border-width) solid ${error ? 'var(--flag-500)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-md)',
          padding: '11px 14px',
          width: '100%',
          ...style,
        }}
      />
      {error && <span className="kz-error-text">{error}</span>}
    </div>
  );
}

export function Card({
  interactive = false,
  style,
  children,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      {...rest}
      style={{
        background: 'var(--surface-card)',
        border: 'var(--border-width) solid var(--border-default)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        padding: 'var(--space-5)',
        transition: interactive ? 'transform var(--dur-base) var(--ease-out), box-shadow var(--dur-base)' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * A page-level failure state — distinct from an empty-but-loaded state.
 * `onRetry` shows a "Try again" button; omit it for explanatory-only states
 * (e.g. "this account has no family") where retrying can't help.
 */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card style={{ background: 'var(--flag-100)', borderColor: 'var(--flag-500)' }}>
      <p style={{ color: 'var(--ink-900)', font: 'var(--type-body)' }}>{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} style={{ marginTop: 'var(--space-3)' }}>
          Try again
        </Button>
      )}
    </Card>
  );
}

const popColors = ['var(--blue-500)', 'var(--sun-400)', 'var(--coral-500)', 'var(--mint-500)', 'var(--grape-500)'];
export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const color = popColors[name.charCodeAt(0) % popColors.length];
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-pill)',
        background: color,
        color: '#fff',
        display: 'inline-grid',
        placeItems: 'center',
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
}

const statusColors = {
  online: 'var(--status-online)',
  away: 'var(--status-away)',
  busy: 'var(--status-busy)',
  offline: 'var(--status-offline)',
} as const;
export function StatusDot({ status, size = 10 }: { status: keyof typeof statusColors; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: statusColors[status],
        display: 'inline-block',
        boxShadow: '0 0 0 2px var(--white)',
      }}
    />
  );
}

type BadgeTone = 'safe' | 'warn' | 'flag' | 'neutral' | 'brand';
const badgeTones: Record<BadgeTone, React.CSSProperties> = {
  safe: { background: 'var(--safe-100)', color: 'var(--safe-700)' },
  warn: { background: 'var(--warn-100)', color: 'var(--warn-500)' },
  flag: { background: 'var(--flag-100)', color: 'var(--flag-500)' },
  neutral: { background: 'var(--ink-100)', color: 'var(--ink-600)' },
  brand: { background: 'var(--blue-100)', color: 'var(--blue-700)' },
};
export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: React.ReactNode }) {
  return (
    <span
      style={{
        ...badgeTones[tone],
        font: 'var(--type-label)',
        padding: '3px 10px',
        borderRadius: 'var(--radius-pill)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {children}
    </span>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="kz-row" style={{ cursor: 'pointer', justifyContent: 'space-between' }}>
      {label && <span style={{ font: 'var(--type-body)' }}>{label}</span>}
      <span
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onChange(!checked);
        }}
        style={{
          width: 46,
          height: 28,
          borderRadius: 'var(--radius-pill)',
          background: checked ? 'var(--blue-500)' : 'var(--ink-200)',
          position: 'relative',
          transition: 'background var(--dur-base) var(--ease-out)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 3,
            left: checked ? 21 : 3,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: 'var(--shadow-sm)',
            transition: 'left var(--dur-base) var(--ease-bounce)',
          }}
        />
      </span>
    </label>
  );
}

export function ChatBubble({
  mine = false,
  author,
  time,
  variant = 'default',
  children,
}: {
  mine?: boolean;
  author?: string;
  time?: string;
  variant?: 'default' | 'system';
  children: React.ReactNode;
}) {
  if (variant === 'system') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
        <span
          style={{
            font: 'var(--type-mono)',
            fontSize: 'var(--text-xs)',
            color: 'var(--ink-400)',
            background: 'var(--bubble-system)',
            padding: '4px 12px',
            borderRadius: 'var(--radius-pill)',
          }}
        >
          {children}
        </span>
      </div>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: mine ? 'flex-end' : 'flex-start',
        gap: 2,
        maxWidth: '76%',
        alignSelf: mine ? 'flex-end' : 'flex-start',
      }}
    >
      {author && !mine && (
        <span style={{ font: 'var(--type-label)', color: 'var(--blue-600)', paddingLeft: 12 }}>{author}</span>
      )}
      <div
        style={{
          background: mine ? 'var(--bubble-me)' : 'var(--bubble-them)',
          color: mine ? '#fff' : 'var(--ink-900)',
          border: mine ? 'none' : 'var(--border-width) solid var(--border-default)',
          borderRadius: 'var(--radius-bubble)',
          borderBottomRightRadius: mine ? 'var(--radius-xs)' : 'var(--radius-bubble)',
          borderBottomLeftRadius: mine ? 'var(--radius-bubble)' : 'var(--radius-xs)',
          padding: '9px 14px',
          font: 'var(--type-body)',
          boxShadow: 'var(--shadow-xs)',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
      {time && (
        <span style={{ font: 'var(--type-mono)', fontSize: 'var(--text-2xs)', color: 'var(--ink-400)', padding: '0 10px' }}>
          {time}
        </span>
      )}
    </div>
  );
}

export function Logo({ height = 36 }: { height?: number }) {
  return <img src="/logo-full.svg" alt="KidzIM" height={height} style={{ display: 'block' }} />;
}
